import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, School, ChefHat, Building2, Zap, UtensilsCrossed, CalendarDays, CreditCard, BarChart3, DollarSign, TrendingUp, Printer, AlertTriangle, ClipboardList, Landmark, Brain, Cpu, Globe, Shield } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageEcoPulse = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - EcoPulse CC | Centre de Commande"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="ecopulse" lang="fr" />

      {/* Hero EcoPulse */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Brain size={16} />
            <span>Intelligence Artificielle Propriétaire</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s', color: 'rgba(132, 204, 22, 0.95)', textShadow: '0 0 20px rgba(132, 204, 22, 0.3)', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
            Centre de Commande
          </h1>
          <p className="animate-fade-in-up" style={{ animationDelay: '0.15s', fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, color: '#00D4FF', marginBottom: '1rem', letterSpacing: '0.05em', textShadow: '0 0 10px rgba(0, 212, 255, 0.9), 0 0 30px rgba(0, 212, 255, 0.8), 0 0 60px rgba(0, 212, 255, 0.6), 0 0 100px rgba(0, 212, 255, 0.4), 0 0 150px rgba(0, 212, 255, 0.2)' }}>
            EcoPulse CC
          </p>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.2rem', lineHeight: '1.8' }}>
            Le Centre de Commande conversationnel au coeur de la plateforme EcoLunch
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">4</div>
              <div className="stat-label">Portails intelligents</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">IA</div>
              <div className="stat-label">Langage naturel</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">0</div>
              <div className="stat-label">Menus complexes</div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro EcoPulse */}
      <section className="section" data-testid="ecopulse-intro-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-section">
            <div className="ecopulse-intro">
              <h2>
                <img src="/logo-ecolunch.png" alt="EcoPulse" className="ecopulse-logo" style={{width: '60px', height: '60px'}} />
                EcoPulse CC
              </h2>
              <p className="ecopulse-tagline">
                Fini les clics. Avec EcoPulse, une phrase suffit pour tout controler.
              </p>
              <p>
                EcoPulse est l'intelligence conversationnelle intégrée à chaque portail de la plateforme EcoLunch.
                Au lieu de naviguer dans des menus complexes, <strong>vous écrivez votre demande en langage naturel</strong> et EcoPulse comprend, exécute et confirme instantanément.
              </p>
              <p>
                Modifier une commande, générer un rapport, consulter des statistiques, créer un graphique : 
                <strong>tout devient aussi simple qu'envoyer un message</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages EcoPulse */}
      <section className="section" data-testid="ecopulse-benefits">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoPulse change tout</h2>
            <p className="section-subtitle">
              L'IA qui élimine la complexité logicielle
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Brain size={32} /></div>
              <h3>Langage naturel</h3>
              <p>Écrivez votre demande comme vous enverriez un message texte. EcoPulse comprend le contexte, l'intention et exécute.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Exécution instantanée</h3>
              <p>Pas de navigation, pas de menus déroulants. Une phrase = une action exécutée et confirmée en temps réel.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Cpu size={32} /></div>
              <h3>IA propriétaire</h3>
              <p>Technologie développée et hébergée au Québec, spécialement conçue pour les opérations de repas scolaires.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portails EcoPulse */}
      <section id="portails" className="section" data-testid="ecopulse-portails">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Cinq portails, une seule commande</h2>
            <p className="section-subtitle">
              Chaque acteur a son propre Centre de Commande adapté à sa réalité
            </p>
          </div>

          <div className="ecopulse-section">
            <div className="ecopulse-cards" data-testid="ecopulse-cards">

              {/* Parents */}
              <div className="ecopulse-card" data-testid="ecopulse-card-parents">
                <div className="ecopulse-card-header" style={{color: '#3B82F6'}}>
                  <div className="portal-icon">
                    <Users size={35} />
                  </div>
                  <div>
                    <h3>Parents<span className="portal-name">Centre de Controle Intelligent</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><UtensilsCrossed size={14} style={{display:'inline', verticalAlign:'middle'}} /> COMMANDES & MENUS</h4>
                    <ul>
                      <li><code>Change le repas d'Emma pour la salade jeudi</code></li>
                      <li><code>Annule vendredi pour les deux enfants</code></li>
                      <li><code>Menu de mercredi</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><CalendarDays size={14} style={{display:'inline', verticalAlign:'middle'}} /> ABONNEMENTS</h4>
                    <ul>
                      <li><code>Mets Lucas en 4 jours/semaine à partir de lundi</code></li>
                      <li><code>Active l'ordre aléatoire pour Emma</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><CreditCard size={14} style={{display:'inline', verticalAlign:'middle'}} /> FINANCES</h4>
                    <ul>
                      <li><code>Montre-moi mon solde</code></li>
                      <li><code>Reçu de février</code></li>
                      <li><code>Combien j'ai dépensé ce mois-ci ?</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Traiteurs */}
              <div className="ecopulse-card" data-testid="ecopulse-card-caterers">
                <div className="ecopulse-card-header" style={{color: '#F97316'}}>
                  <div className="portal-icon">
                    <ChefHat size={35} />
                  </div>
                  <div>
                    <h3>Traiteurs<span className="portal-name">Centre de Controle Opérationnel</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><BarChart3 size={14} style={{display:'inline', verticalAlign:'middle'}} /> COMMANDES CONSOLIDÉES</h4>
                    <ul>
                      <li><code>Combien de repas demain pour toutes les écoles ?</code></li>
                      <li><code>Commandes Notre-Dame cette semaine</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><DollarSign size={14} style={{display:'inline', verticalAlign:'middle'}} /> FINANCIER & ANALYTICS</h4>
                    <ul>
                      <li><code>Combien de ventes Sezzle ce mois-ci ?</code></li>
                      <li><code>Augmentation CA depuis EcoLunch</code></li>
                      <li><code>Revenus par école cette semaine</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><TrendingUp size={14} style={{display:'inline', verticalAlign:'middle'}} /> GRAPHIQUES</h4>
                    <ul>
                      <li><code>Graphique revenus par école, par mois, et par type de menu</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><Printer size={14} style={{display:'inline', verticalAlign:'middle'}} /> PRODUCTION</h4>
                    <ul>
                      <li><code>Lance impression étiquettes 8h00 am demain à vendredi toutes écoles</code></li>
                      <li><code>Envoie rapport production à St-Joseph</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Écoles */}
              <div className="ecopulse-card" data-testid="ecopulse-card-schools">
                <div className="ecopulse-card-header" style={{color: '#22C55E'}}>
                  <div className="portal-icon">
                    <School size={35} />
                  </div>
                  <div>
                    <h3>Écoles<span className="portal-name">Centre de Pilotage</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><BarChart3 size={14} style={{display:'inline', verticalAlign:'middle'}} /> SUIVI & PARTICIPATION</h4>
                    <ul>
                      <li><code>Combien d'élèves ont commandé aujourd'hui ?</code></li>
                      <li><code>Taux de participation de ma 3e année</code></li>
                      <li><code>Classe avec le meilleur taux</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><AlertTriangle size={14} style={{display:'inline', verticalAlign:'middle'}} /> SIGNALEMENTS</h4>
                    <ul>
                      <li><code>Élèves allergiques aux arachides</code></li>
                      <li><code>Qui n'a pas commandé cette semaine ?</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><ClipboardList size={14} style={{display:'inline', verticalAlign:'middle'}} /> RAPPORTS</h4>
                    <ul>
                      <li><code>Rapport fréquentation pour le conseil</code></li>
                      <li><code>Satisfaction moyenne ce mois-ci</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Directions */}
              <div className="ecopulse-card" data-testid="ecopulse-card-directions">
                <div className="ecopulse-card-header" style={{color: '#A855F7'}}>
                  <div className="portal-icon">
                    <Building2 size={35} />
                  </div>
                  <div>
                    <h3>Directions<span className="portal-name">Centre de Gouvernance</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><Landmark size={14} style={{display:'inline', verticalAlign:'middle'}} /> VUE CONSOLIDÉE</h4>
                    <ul>
                      <li><code>Participation globale toutes écoles ce mois-ci</code></li>
                      <li><code>Quelle école a le meilleur taux ?</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><DollarSign size={14} style={{display:'inline', verticalAlign:'middle'}} /> DÉCISIONNEL</h4>
                    <ul>
                      <li><code>Compare coûts par repas entre traiteurs</code></li>
                      <li><code>Prévisions budget Q2</code></li>
                      <li><code>Coût total du réseau cette année</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><Landmark size={14} style={{display:'inline', verticalAlign:'middle'}} /> DOCUMENTS</h4>
                    <ul>
                      <li><code>Rapport annuel pour la commission</code></li>
                      <li><code>Export facturation 2024-2025</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* CSS (siège) */}
              <div className="ecopulse-card" data-testid="ecopulse-card-css">
                <div className="ecopulse-card-header" style={{color: '#E74C3C'}}>
                  <div className="portal-icon">
                    <Landmark size={35} />
                  </div>
                  <div>
                    <h3>CSS (siège)<span className="portal-name">Centre de Gouvernance Globale</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><Globe size={14} style={{display:'inline', verticalAlign:'middle'}} /> VUE GLOBALE</h4>
                    <ul>
                      <li><code>Performance globale toutes écoles ce trimestre</code></li>
                      <li><code>Compare conformité nutritionnelle par direction</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><BarChart3 size={14} style={{display:'inline', verticalAlign:'middle'}} /> ANALYTICS & BI</h4>
                    <ul>
                      <li><code>Rapport annuel consolidé pour le conseil</code></li>
                      <li><code>Tendances participation réseau 3 ans</code></li>
                      <li><code>KPIs durabilité réseau</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><Shield size={14} style={{display:'inline', verticalAlign:'middle'}} /> GOUVERNANCE</h4>
                    <ul>
                      <li><code>Alertes critiques réseau cette semaine</code></li>
                      <li><code>Audit conformité toutes directions</code></li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>
              <div className="footer-column">
                <h4>Technologies</h4>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
                <a onClick={() => navigate('/platform')}>Plateforme</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageEcoPulse;
